import { useCallback, useEffect, useState } from "react";
import { Form, Switch, InputNumber, Flex, Tooltip, Select, Button, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { IMasterDataSupplier, IMasterDataType } from "../../../interfaces/master-data";
import {
  ERuleDefaultNominalCodesDefault,
  ERuleDefaultPaymentTolerancesDefault,
  ERuleDefaultSettingsDefault,
} from "../../../constants/enum";
import Title from "antd/es/typography/Title";
import { SystemDefaultServices } from "../../../services/system-default";
import { MasterDataServices } from "../../../services/master-data";
import { useMessageApi } from "../../../context/MessageContext";
import { PERMISSIONS } from "../../../constants/permission";
import { useHasPermission } from "../../../hooks/useHasPermission";

type NamePath = string | number | (string | number)[];

const RuleDefaultPage = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const [loading, setLoading] = useState(false);
  const [loadingScreen, setLoadingScreen] = useState(true);
  const hasSystemDefaultEditor = useHasPermission([PERMISSIONS.SYSTEM_DEFAULT_EDITOR]);
  const [masterData, setMasterData] = useState<IMasterDataSupplier>({
    accountDebits: [],
    accountCredits: [],
    autosMatch: [],
    users: [],
    defaultTermsRule: {},
  });

  const { accountCredits, accountDebits, autosMatch, defaultTermsRule } = masterData;

  const fetchMasterData = async () => {
    setLoadingScreen(true);
    try {
      const types = [
        "PAYMENT_TERM_RULE_NOMINAL_ACCOUNT_DEBIT",
        "PAYMENT_TERM_RULE_NOMINAL_ACCOUNT_CREDIT",
        "PAYMENT_TERM_RULE_UNDERS_OVERS_AUTO_MATCH",
      ];

      const masterPromises = types.map((type) => MasterDataServices.getAllByType(type).then((res) => res?.data || []));

      const [accountDebits, accountCredits, autosMatch, defaultTermsRule] = await Promise.all([
        ...masterPromises,
        SystemDefaultServices.getDefaultTermsRule({ type: "RULE" }).then((res) => res?.data || {}),
      ]);

      setMasterData({
        accountDebits,
        accountCredits,
        autosMatch,
        defaultTermsRule,
      });
    } catch (error) {
      console.error("Error fetching master data:", error);
      messageApi.error("Failed to load master data.");
    } finally {
      setLoadingScreen(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (defaultTermsRule && Object.keys(defaultTermsRule).length > 0) {
      form.setFieldsValue({ ...defaultTermsRule });
    }
  }, [defaultTermsRule, form]);

  const getName = (field: string): NamePath => field;

  const getValue = (getFieldValue: (path: NamePath) => any, field: string): any => {
    const namePath = [field];
    return getFieldValue(namePath);
  };

  const getOptionsByField = useCallback(
    (fieldName: string): IMasterDataType[] => {
      switch (fieldName) {
        case "sageAccountDebit":
          return accountDebits ?? [];
        case "sageAccountCredit":
          return accountCredits ?? [];
        case "undersOversAutoMatchCode":
          return autosMatch ?? [];
        default:
          return [];
      }
    },
    [accountDebits, accountCredits, autosMatch]
  );

  const renderSelect = (fieldName: string, disabled: boolean) => {
    const options = getOptionsByField(fieldName);
    return (
      <Select
        disabled={disabled}
        showSearch
        allowClear
        placeholder="Select"
        filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        options={options.map(({ code, label }) => ({ value: code, label }))}
      />
    );
  };

  const renderSwitchExtraInput = (field: any, fieldAll: string, getFieldValue: (name: NamePath) => any) => {
    const isFieldAllActive = getValue(getFieldValue, fieldAll);
    const isFieldActive = getValue(getFieldValue, field.name);
    const isDisabled = isFieldAllActive || !isFieldActive;

    const handleSwitchChange = (checked: boolean) => {
      if (!checked) {
        if (field.extraInput) {
          form.setFieldValue(getName(field.extraInput), undefined);
        }
        if (field.selectInput) {
          form.setFieldValue(getName(field.selectInput), undefined);
        }
      }
    };

    return (
      <Flex style={{ width: "200px" }} gap={12}>
        <Form.Item name={getName(field.name)} valuePropName="checked">
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            disabled={isFieldAllActive && fieldAll !== field.name}
            onChange={handleSwitchChange}
          />
        </Form.Item>

        {field.extraInput && (
          <Form.Item
            name={getName(field.extraInput)}
            rules={[
                  {
                    validator: (_, value) => {
                      if(isDisabled){
                        return Promise.resolve();
                      }

                      if (field?.typeExtraInput === 'percentage') {
                        if (!value || value < 1 || value > 100) {
                          return Promise.reject(new Error('Must be between 1 and 100'));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} disabled={isDisabled} />
          </Form.Item>
        )}

        {field.selectInput && (
          <Form.Item name={getName(field.selectInput)} style={{ width: "100%" }}>
            {renderSelect(field.selectInput, isDisabled)}
          </Form.Item>
        )}
      </Flex>
    );
  };

  const renderBox = (rules: any[], fieldAll: string) => (
    <>
      {rules.map((field, index) => (
        <Flex
          key={field.name}
          justify="space-between"
          align="center"
          className={`item-rule ${index === 0 ? "last" : ""}`}
        >
          {field.name !== fieldAll ? (
            <>
              <Flex align="center" gap={6} className="label">
                <span>{field.label}</span>
                {field.tooltip && (
                  <Tooltip title={field.tooltip}>
                    <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                  </Tooltip>
                )}
              </Flex>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => renderSwitchExtraInput(field, fieldAll, getFieldValue)}
              </Form.Item>
            </>
          ) : (
            <Flex align="center" justify="center" gap={6} className="label-title">
              <Title level={5} style={{ marginTop: 9 }}>
                {field.label}
              </Title>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                </Tooltip>
              )}
            </Flex>
          )}
        </Flex>
      ))}
    </>
  );

  const handleSubmitAll = async () => {
    try {
      const ruleValues = await form.validateFields();

      const payload = {
        ...ruleValues,
      };

      setLoading(true);
      const response: any = await SystemDefaultServices.updateSystemDefaultRule(payload);

      if (response.success) {
        messageApi.success(response.message);
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const onChangeForm = (changedValues: any, allValues: any) => {
      // check render one vaue between {isBspCost, isTourEvent, isOtherSupplier}
      if (changedValues.isBspCost) {
        allValues["isTourEvent"] = false;
        allValues["isOtherSupplier"] = false;
      } else if (changedValues.isTourEvent) {
        allValues["isBspCost"] = false;
        allValues["isOtherSupplier"] = false;
      } else if (changedValues.isOtherSupplier) {
        allValues["isBspCost"] = false;
        allValues["isTourEvent"] = false;
      }
      form.setFieldsValue(allValues);
    };

  if (loadingScreen) {
    return (
      <Flex gap="middle" vertical style={{ padding: "2rem" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Form form={form} onValuesChange={onChangeForm}>
      <Flex vertical>
        {hasSystemDefaultEditor ? (
          <Flex gap={16} style={{ marginBottom: 15 }} justify="end">
            <Button type="primary" onClick={handleSubmitAll} loading={loading}>
              Save
            </Button>
          </Flex>
        ) : null}
        <div className="box-rule">
          {renderBox(ERuleDefaultSettingsDefault, ERuleDefaultSettingsDefault[0].name)}
          {renderBox(ERuleDefaultPaymentTolerancesDefault, ERuleDefaultPaymentTolerancesDefault[0].name)}
          {renderBox(ERuleDefaultNominalCodesDefault, ERuleDefaultNominalCodesDefault[0].name)}
        </div>
      </Flex>
    </Form>
  );
};

export default RuleDefaultPage;
